import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Chip,
  Alert,
  Paper,
} from '@mui/material';
import {
  Star,
  Lock,
} from '@mui/icons-material';
import ServiceAuth from '../../components/ServiceAuth';
import ServiceGrid from '../../components/services/ServiceGrid';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ServiceIntent } from '../../config/services.config';

const ServicesPortal: React.FC = () => {
  const { t } = useTranslation();
  const [isServiceAuthenticated, setIsServiceAuthenticated] = useState(false);
  const [serviceUserData, setServiceUserData] = useState<any>(null);
  const navigate = useNavigate();
  
  const user = useSelector((state: RootState) => state.auth.user);
  const isLoggedIn = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isPilot = user?.is_pilot;

  // Check for existing service authentication
  useEffect(() => {
    const existingServiceToken = localStorage.getItem('serviceToken');
    const existingServiceData = localStorage.getItem('serviceUserData');
    
    if (existingServiceToken && existingServiceData) {
      setIsServiceAuthenticated(true);
      setServiceUserData(JSON.parse(existingServiceData));
    }
  }, []);

  const handleAuthenticated = (userData: any) => {
    setIsServiceAuthenticated(true);
    setServiceUserData(userData);
  };

  const handleServiceSelect = (serviceId: string) => {
    // Navigate directly to the service - authentication will be handled there if needed
    if (serviceId === 'equipment') {
      navigate('/app/reminders/dashboard');
    } else if (serviceId === 'shuttle') {
      navigate('/app/shuttle-booking');
    } else if (serviceId === 'marketplace') {
      navigate('/app/marketplace');
    } else {
      navigate(`/${serviceId}`);
    }
  };

  // Determine user tier
  const getUserTier = () => {
    if (isPilot || serviceUserData?.has_premium_access) {
      return 'premium';
    }
    return 'free';
  };

  // If not authenticated at all, show auth
  if (!isServiceAuthenticated && !isLoggedIn) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom>
            {t('services.portal.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('services.portal.description')}
          </Typography>
        </Paper>
        
        <ServiceAuth 
          onAuthenticated={handleAuthenticated}
          intent={null}
        />
      </Container>
    );
  }

  const userTier = getUserTier();
  const displayName = serviceUserData?.nickname || serviceUserData?.name || user?.name || 'User';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom>
          {t('services.portal.title')}
        </Typography>
        
        {userTier === 'premium' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Chip 
              icon={<Star />} 
              label={t('services.portal.premiumAccess')} 
              color="primary" 
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
        )}
        
        <Typography variant="body1" color="text.secondary">
          {t('services.portal.welcome', { name: displayName })}
        </Typography>
      </Paper>

      {/* Service-specific authentication info */}
      {serviceUserData && !isLoggedIn && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('services.portal.serviceUserInfo')}
          </Typography>
        </Alert>
      )}

      {/* Platform user with premium access */}
      {isPilot && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('services.portal.pilotInfo')}
          </Typography>
        </Alert>
      )}

      {/* Free tier limitations */}
      {userTier === 'free' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{t('services.portal.freeTier.title')}</strong>
            <br />
            {t('services.portal.freeTier.description')}
          </Typography>
        </Alert>
      )}

      {/* Services Grid */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          {t('services.portal.availableServices')}
        </Typography>
        
        <ServiceGrid 
          onServiceSelect={handleServiceSelect}
          userTier={userTier}
        />
      </Box>

      {/* Upgrade prompt for free users */}
      {userTier === 'free' && (
        <Paper sx={{ p: 3, mt: 4, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Lock />
            <Box>
              <Typography variant="h6">
                {t('services.portal.upgrade.title')}
              </Typography>
              <Typography variant="body2">
                {t('services.portal.upgrade.description')}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default ServicesPortal;